import React, { useState, useEffect } from 'react';
import { Instagram, MessageCircle, Heart, Bookmark, Send, Hash, AtSign, Link, Image, MapPin, Phone, Mail, Globe, Save, Copy, Trash2, Plus, Edit2, Eye, EyeOff, Smartphone, Monitor, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Template {
  id: string;
  name: string;
  trigger: 'comment' | 'dm' | 'mention' | 'story_reply';
  conditions: {
    keywords?: string[];
    hashtags?: string[];
    userType?: 'any' | 'follower' | 'verified' | 'business';
    postType?: 'any' | 'specific' | 'next';
  };
  response: {
    text: string;
    variables: string[];
    includeUsername: boolean;
    addEmojis: boolean;
    buttons?: Array<{
      label: string;
      action: string;
      value: string;
    }>;
  };
  language: 'pt' | 'en' | 'both';
  active: boolean;
  testMode: boolean;
}

interface PreviewData {
  username: string;
  postCaption: string;
  commentText: string;
  followerCount: number;
  isVerified: boolean;
}

export const InstagramTemplateBuilder: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<Template>({
    id: '',
    name: '',
    trigger: 'comment',
    conditions: {
      keywords: [],
      hashtags: [],
      userType: 'any',
      postType: 'any'
    },
    response: {
      text: '',
      variables: [],
      includeUsername: true,
      addEmojis: true,
      buttons: []
    },
    language: 'pt',
    active: false,
    testMode: true
  });

  const [previewData, setPreviewData] = useState<PreviewData>({
    username: 'ballbriane',
    postCaption: '🌴 Mais um dia no paraíso! Praia dos Carneiros te espera ☀️ #CarneirosParadiso #Pernambuco',
    commentText: 'Que lugar incrível! Como faço para reservar?',
    followerCount: 1250,
    isVerified: false
  });

  const [showPreview, setShowPreview] = useState(true);
  const [deviceView, setDeviceView] = useState<'mobile' | 'desktop'>('mobile');
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const [hashtag, setHashtag] = useState('');
  const [savedStatus, setSavedStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Carneiros Paradiso default templates
  const defaultTemplates: Template[] = [
    {
      id: '1',
      name: 'Resposta de Boas-Vindas',
      trigger: 'comment',
      conditions: {
        keywords: ['reserva', 'disponibilidade', 'preço', 'valor'],
        userType: 'any',
        postType: 'any'
      },
      response: {
        text: '@{username} 🌴 Olá! Que bom que você se interessou pelo Carneiros Paradiso!\n\n📅 Para verificar disponibilidade e fazer sua reserva, acesse nosso link na bio ou envie uma DM.\n\n💬 Resposta automática - Nossa equipe entrará em contato em breve!',
        variables: ['username'],
        includeUsername: true,
        addEmojis: true,
        buttons: []
      },
      language: 'pt',
      active: true,
      testMode: false
    },
    {
      id: '2',
      name: 'Welcome Response (English)',
      trigger: 'comment',
      conditions: {
        keywords: ['booking', 'availability', 'price', 'reservation'],
        userType: 'any',
        postType: 'any'
      },
      response: {
        text: '@{username} 🌴 Welcome to Carneiros Paradiso!\n\n📅 To check availability and make your reservation, visit our link in bio or send us a DM.\n\n💬 Automated response - Our team will contact you soon!',
        variables: ['username'],
        includeUsername: true,
        addEmojis: true,
        buttons: []
      },
      language: 'en',
      active: true,
      testMode: false
    },
    {
      id: '3',
      name: 'Resposta para DM',
      trigger: 'dm',
      conditions: {
        keywords: [],
        userType: 'any'
      },
      response: {
        text: 'Olá {username}! 🌴\n\nObrigado por entrar em contato com o Carneiros Paradiso!\n\nPara agilizar seu atendimento:\n📅 Reservas: biig.dev/booking\n📞 WhatsApp: +55 81 99999-9999\n📍 Localização: Praia dos Carneiros, PE\n\nNossa equipe responderá em breve!',
        variables: ['username'],
        includeUsername: false,
        addEmojis: true,
        buttons: []
      },
      language: 'pt',
      active: true,
      testMode: false
    }
  ];

  useEffect(() => {
    // Load saved templates or use defaults
    const saved = localStorage.getItem('instagram-templates');
    if (saved) {
      setTemplates(JSON.parse(saved));
    } else {
      setTemplates(defaultTemplates);
    }
  }, []);

  const processTemplate = (template: Template, data: PreviewData): string => {
    let text = template.response.text;
    
    // Replace variables
    if (template.response.includeUsername) {
      text = text.replace(/{username}/g, data.username);
    }
    
    // Add dynamic content based on conditions
    if (template.trigger === 'comment') {
      // Add post-specific context
      if (data.postCaption.includes('#')) {
        const hashtags = data.postCaption.match(/#\w+/g) || [];
        if (hashtags.some(tag => tag === '#CarneirosParadiso')) {
          text += '\n\n🏖️ Vejo que você já conhece nosso paraíso!';
        }
      }
    }
    
    return text;
  };

  const saveTemplate = () => {
    if (!currentTemplate.name || !currentTemplate.response.text) {
      alert('Por favor, preencha o nome e o texto da resposta');
      return;
    }

    setSavedStatus('saving');
    
    const newTemplate: Template = {
      ...currentTemplate,
      id: currentTemplate.id || Date.now().toString()
    };

    let updatedTemplates;
    if (editingTemplate) {
      updatedTemplates = templates.map(t => 
        t.id === editingTemplate ? newTemplate : t
      );
      setEditingTemplate(null);
    } else {
      updatedTemplates = [...templates, newTemplate];
    }

    setTemplates(updatedTemplates);
    localStorage.setItem('instagram-templates', JSON.stringify(updatedTemplates));
    
    // Reset form
    setCurrentTemplate({
      id: '',
      name: '',
      trigger: 'comment',
      conditions: {
        keywords: [],
        hashtags: [],
        userType: 'any',
        postType: 'any'
      },
      response: {
        text: '',
        variables: [],
        includeUsername: true,
        addEmojis: true,
        buttons: []
      },
      language: 'pt',
      active: false,
      testMode: true
    });

    setSavedStatus('saved');
    setTimeout(() => setSavedStatus('idle'), 2000);
  };

  const deleteTemplate = (id: string) => {
    const updatedTemplates = templates.filter(t => t.id !== id);
    setTemplates(updatedTemplates);
    localStorage.setItem('instagram-templates', JSON.stringify(updatedTemplates));
  };

  const editTemplate = (template: Template) => {
    setCurrentTemplate(template);
    setEditingTemplate(template.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addKeyword = () => {
    if (keyword && !currentTemplate.conditions.keywords?.includes(keyword)) {
      setCurrentTemplate({
        ...currentTemplate,
        conditions: {
          ...currentTemplate.conditions,
          keywords: [...(currentTemplate.conditions.keywords || []), keyword]
        }
      });
      setKeyword('');
    }
  };

  const removeKeyword = (kw: string) => {
    setCurrentTemplate({
      ...currentTemplate,
      conditions: {
        ...currentTemplate.conditions,
        keywords: currentTemplate.conditions.keywords?.filter(k => k !== kw)
      }
    });
  };

  const addHashtag = () => {
    const formattedHashtag = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
    if (formattedHashtag && !currentTemplate.conditions.hashtags?.includes(formattedHashtag)) {
      setCurrentTemplate({
        ...currentTemplate,
        conditions: {
          ...currentTemplate.conditions,
          hashtags: [...(currentTemplate.conditions.hashtags || []), formattedHashtag]
        }
      });
      setHashtag('');
    }
  };

  const removeHashtag = (tag: string) => {
    setCurrentTemplate({
      ...currentTemplate,
      conditions: {
        ...currentTemplate.conditions,
        hashtags: currentTemplate.conditions.hashtags?.filter(h => h !== tag)
      }
    });
  };

  const toggleTemplateStatus = (id: string) => {
    const updatedTemplates = templates.map(t => 
      t.id === id ? { ...t, active: !t.active } : t
    );
    setTemplates(updatedTemplates);
    localStorage.setItem('instagram-templates', JSON.stringify(updatedTemplates));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                <Instagram className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Instagram Template Builder</h1>
                <p className="text-gray-600">Carneiros Paradiso - Automação de Respostas</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {showPreview ? <EyeOff size={20} /> : <Eye size={20} />}
                <span>Preview</span>
              </button>
              
              <div className="flex items-center bg-gray-100 rounded-lg">
                <button
                  onClick={() => setDeviceView('mobile')}
                  className={`p-2 rounded-lg ${deviceView === 'mobile' ? 'bg-white shadow' : ''}`}
                >
                  <Smartphone size={20} />
                </button>
                <button
                  onClick={() => setDeviceView('desktop')}
                  className={`p-2 rounded-lg ${deviceView === 'desktop' ? 'bg-white shadow' : ''}`}
                >
                  <Monitor size={20} />
                </button>
              </div>

              {savedStatus === 'saved' && (
                <div className="flex items-center gap-2 text-green-600">
                  <Check size={20} />
                  <span className="text-sm">Salvo!</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Template Builder */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingTemplate ? 'Editar Template' : 'Criar Novo Template'}
            </h2>

            {/* Template Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Template
              </label>
              <input
                type="text"
                value={currentTemplate.name}
                onChange={(e) => setCurrentTemplate({...currentTemplate, name: e.target.value})}
                placeholder="Ex: Resposta de Boas-Vindas"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Trigger Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Gatilho
              </label>
              <select
                value={currentTemplate.trigger}
                onChange={(e) => setCurrentTemplate({...currentTemplate, trigger: e.target.value as any})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="comment">Comentário em Post</option>
                <option value="dm">Mensagem Direta (DM)</option>
                <option value="mention">Menção (@)</option>
                <option value="story_reply">Resposta ao Story</option>
              </select>
            </div>

            {/* Language */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Idioma
              </label>
              <select
                value={currentTemplate.language}
                onChange={(e) => setCurrentTemplate({...currentTemplate, language: e.target.value as any})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="pt">Português</option>
                <option value="en">English</option>
                <option value="both">Detectar Automaticamente</option>
              </select>
            </div>

            {/* Keywords */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Palavras-chave (ativam a resposta)
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                  placeholder="Ex: reserva, preço, disponibilidade"
                  className="flex-1 px-3 py-2 border rounded-lg"
                />
                <button
                  onClick={addKeyword}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                >
                  <Plus size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {currentTemplate.conditions.keywords?.map((kw, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1"
                  >
                    {kw}
                    <button onClick={() => removeKeyword(kw)}>
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Hashtags */}
            {currentTemplate.trigger === 'comment' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hashtags do Post
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={hashtag}
                    onChange={(e) => setHashtag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addHashtag()}
                    placeholder="Ex: #CarneirosParadiso"
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <button
                    onClick={addHashtag}
                    className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
                  >
                    <Hash size={20} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {currentTemplate.conditions.hashtags?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm flex items-center gap-1"
                    >
                      {tag}
                      <button onClick={() => removeHashtag(tag)}>
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Response Text */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texto da Resposta
              </label>
              <textarea
                value={currentTemplate.response.text}
                onChange={(e) => setCurrentTemplate({
                  ...currentTemplate,
                  response: { ...currentTemplate.response, text: e.target.value }
                })}
                placeholder="Digite a mensagem de resposta automática...
                
Variáveis disponíveis:
{username} - Nome do usuário
{post_url} - Link do post
{date} - Data atual"
                rows={6}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Options */}
            <div className="mb-4 space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentTemplate.response.includeUsername}
                  onChange={(e) => setCurrentTemplate({
                    ...currentTemplate,
                    response: { ...currentTemplate.response, includeUsername: e.target.checked }
                  })}
                  className="rounded"
                />
                <span className="text-sm">Incluir @username na resposta</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentTemplate.response.addEmojis}
                  onChange={(e) => setCurrentTemplate({
                    ...currentTemplate,
                    response: { ...currentTemplate.response, addEmojis: e.target.checked }
                  })}
                  className="rounded"
                />
                <span className="text-sm">Adicionar emojis automaticamente</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentTemplate.testMode}
                  onChange={(e) => setCurrentTemplate({...currentTemplate, testMode: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Modo de teste (não envia respostas reais)</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={saveTemplate}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg"
              >
                <Save size={20} className="inline mr-2" />
                {editingTemplate ? 'Atualizar' : 'Salvar'} Template
              </button>
              
              {editingTemplate && (
                <button
                  onClick={() => {
                    setEditingTemplate(null);
                    setCurrentTemplate({
                      id: '',
                      name: '',
                      trigger: 'comment',
                      conditions: {
                        keywords: [],
                        hashtags: [],
                        userType: 'any',
                        postType: 'any'
                      },
                      response: {
                        text: '',
                        variables: [],
                        includeUsername: true,
                        addEmojis: true,
                        buttons: []
                      },
                      language: 'pt',
                      active: false,
                      testMode: true
                    });
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Preview em Tempo Real</h2>

              {/* Preview Settings */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium mb-2">Dados de Teste</h3>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={previewData.username}
                    onChange={(e) => setPreviewData({...previewData, username: e.target.value})}
                    placeholder="Username"
                    className="w-full px-3 py-1 border rounded text-sm"
                  />
                  <textarea
                    value={previewData.commentText}
                    onChange={(e) => setPreviewData({...previewData, commentText: e.target.value})}
                    placeholder="Texto do comentário"
                    rows={2}
                    className="w-full px-3 py-1 border rounded text-sm"
                  />
                </div>
              </div>

              {/* Instagram Mock */}
              <div className={`${deviceView === 'mobile' ? 'max-w-sm' : 'max-w-full'} mx-auto`}>
                <div className="bg-black rounded-3xl p-2">
                  <div className="bg-white rounded-2xl overflow-hidden">
                    {/* Instagram Header */}
                    <div className="flex items-center justify-between p-3 border-b">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
                        <span className="font-semibold text-sm">carneirosparadiso</span>
                        {previewData.isVerified && <span className="text-blue-500">✓</span>}
                      </div>
                      <button className="text-gray-500">•••</button>
                    </div>

                    {/* Post Image */}
                    <div className="bg-gradient-to-br from-blue-200 via-cyan-200 to-green-200 h-80 flex items-center justify-center">
                      <div className="text-center text-white">
                        <MapPin size={48} className="mx-auto mb-2" />
                        <p className="font-semibold">Praia dos Carneiros</p>
                        <p className="text-sm opacity-90">Pernambuco, Brasil</p>
                      </div>
                    </div>

                    {/* Post Actions */}
                    <div className="p-3">
                      <div className="flex justify-between mb-3">
                        <div className="flex gap-4">
                          <Heart className="w-6 h-6" />
                          <MessageCircle className="w-6 h-6" />
                          <Send className="w-6 h-6" />
                        </div>
                        <Bookmark className="w-6 h-6" />
                      </div>

                      {/* Caption */}
                      <div className="mb-2">
                        <span className="font-semibold text-sm">carneirosparadiso</span>
                        <span className="text-sm ml-2">{previewData.postCaption}</span>
                      </div>

                      {/* Comments Section */}
                      <div className="text-sm text-gray-500 mb-2">Ver todos os comentários</div>
                      
                      {/* User Comment */}
                      <div className="mb-2">
                        <span className="font-semibold text-sm">{previewData.username}</span>
                        <span className="text-sm ml-2">{previewData.commentText}</span>
                      </div>

                      {/* Template Response Preview */}
                      {currentTemplate.response.text && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mb-2 p-2 bg-gray-50 rounded-lg"
                        >
                          <span className="font-semibold text-sm text-purple-600">carneirosparadiso</span>
                          <p className="text-sm mt-1 whitespace-pre-wrap">
                            {processTemplate(currentTemplate, previewData)}
                          </p>
                          {currentTemplate.testMode && (
                            <span className="text-xs text-orange-500 mt-1 block">
                              ⚠️ Modo de teste - não será enviado
                            </span>
                          )}
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Saved Templates */}
        <div className="mt-6 bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Templates Salvos</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className={`border rounded-lg p-4 ${
                  template.active ? 'border-green-500 bg-green-50' : 'border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold">{template.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        template.trigger === 'comment' ? 'bg-purple-100 text-purple-700' :
                        template.trigger === 'dm' ? 'bg-blue-100 text-blue-700' :
                        template.trigger === 'mention' ? 'bg-pink-100 text-pink-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {template.trigger}
                      </span>
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                        {template.language === 'pt' ? '🇧🇷 PT' : template.language === 'en' ? '🇺🇸 EN' : '🌐 Auto'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleTemplateStatus(template.id)}
                    className={`p-2 rounded-lg ${
                      template.active ? 'bg-green-500 text-white' : 'bg-gray-300'
                    }`}
                  >
                    {template.active ? '✓' : '○'}
                  </button>
                </div>
                
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                  {template.response.text}
                </p>
                
                {template.conditions.keywords && template.conditions.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {template.conditions.keywords.slice(0, 3).map((kw, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-purple-100 rounded">
                        {kw}
                      </span>
                    ))}
                    {template.conditions.keywords.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{template.conditions.keywords.length - 3}
                      </span>
                    )}
                  </div>
                )}
                
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => editTemplate(template)}
                    className="flex-1 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    <Edit2 size={14} className="inline mr-1" />
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(template.response.text);
                      alert('Template copiado!');
                    }}
                    className="px-3 py-1 bg-gray-200 rounded text-sm hover:bg-gray-300"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={() => deleteTemplate(template.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add X icon component if not imported
const X: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);